import { Chart as ChartJS, TooltipItem, Plugin } from 'chart.js';

export const createCenterCirclePlugins = (isDarkMode: boolean) => ({
  centerCircle1: {
    id: 'centerCircle1',
    afterDraw: (chart: ChartJS) => {
      const ctx = chart.ctx;
      if (!ctx) return;
      const radarScale = chart.scales.r;
      if (
        !radarScale ||
        radarScale.xCenter === undefined ||
        radarScale.yCenter === undefined
      )
        return;

      const centerPoint = {
        x: radarScale.xCenter,
        y: radarScale.yCenter,
      };

      const radius = (radarScale.drawingArea * 3) / 10;
      ctx.save();

      ctx.fillStyle = isDarkMode
        ? 'rgba(255, 255, 255, 0.05)'
        : 'rgba(99, 133, 254, 0.05)';

      ctx.beginPath();
      ctx.arc(centerPoint.x, centerPoint.y, radius, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    },
  },
  centerCircle2: {
    id: 'centerCircle2',
    afterDraw: (chart: ChartJS) => {
      const ctx = chart.ctx;
      if (!ctx) return;
      const radarScale = chart.scales.r;
      if (
        !radarScale ||
        radarScale.xCenter === undefined ||
        radarScale.yCenter === undefined
      )
        return;

      const centerPoint = {
        x: radarScale.xCenter,
        y: radarScale.yCenter,
      };

      const radius = (radarScale.drawingArea * 6.5) / 10;
      ctx.save();

      ctx.fillStyle = isDarkMode
        ? 'rgba(255, 255, 255, 0.05)'
        : 'rgba(99, 133, 254, 0.05)';

      ctx.beginPath();
      ctx.arc(centerPoint.x, centerPoint.y, radius, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    },
  },
  centerCircle3: {
    id: 'centerCircle3',
    afterDraw: (chart: ChartJS) => {
      const ctx = chart.ctx;
      if (!ctx) return;
      const radarScale = chart.scales.r;
      if (
        !radarScale ||
        radarScale.xCenter === undefined ||
        radarScale.yCenter === undefined
      )
        return;

      const centerPoint = {
        x: radarScale.xCenter,
        y: radarScale.yCenter,
      };

      const radius = radarScale.drawingArea;
      ctx.save();

      ctx.fillStyle = isDarkMode
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(99, 133, 254, 0.05)';

      ctx.beginPath();
      ctx.arc(centerPoint.x, centerPoint.y, radius, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    },
  },
});

export const formatTopicRadarLabels = (str: string) => {
  const shortLabels = {
    'Biochemistry, Genetics, & Metabolism': 'Basic',
    'Biostatistics & Epidemiology': 'Biostats',
    'Blood & Lymphatic System': 'Heme',
    'Cardiovascular System': 'Cardio',
    Dermatology: 'Derm',
    'Endocrine System': 'Endo',
    'Ethics & Healthcare Systems': 'Ethics',
    'Gastrointestinal System': 'GI',
    'Immune System': 'Immune',
    'Infectious Disease': 'ID',
    'Musculoskeletal System': 'MSK',
    'Nervous System': 'Neuro',
    'Psychiatry & Behavioral Health': 'Pysch',
    'Renal & Urinary System': 'Renal',
    'Reproductive Systems & Breast': 'Repro',
    'Respiratory System': 'Pulm',
    'Multisystem Processes': 'Multi',
    'Special Senses & ENT': 'ENT',
    'Anatomy & Embryology': 'Anatomy',
    Pharmacology: 'Pharm',
    'Pathology & Pathophysiology': 'Path',
    Microbiology: 'Micro',
    'Cell & Molecular Biology': 'Cell',
    Immunology: 'Immuno',
    'Biochemistry & Metabolism': 'Basic',
    Physiology: 'Phys',
    Histology: 'Histo',
    'Emergency Medicine': 'Emergency',
    'Obstetrics & Gynecology': 'OBGYN',
    'Family Medicine': 'Family',
    'Internal Medicine': 'Internal',
  };

  // A course module ("Unit 3: Cells") keeps only its number on the axis;
  // the full title is in the hover tooltip.
  const unitMatch = /^(Unit \d+):/.exec(str);
  if (unitMatch) return unitMatch[1];

  return shortLabels[str as keyof typeof shortLabels] || str;
};

export const topicRadarOptions = (
  isDarkMode: boolean,
  formatTooltipValue: (value: number) => string = (value) =>
    `Performance: ${value.toFixed(0)}%`
) => ({
  responsive: true,
  maintainAspectRatio: false,
  layout: {
    padding: {
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
    },
  },
  elements: {
    line: {
      borderWidth: 1,
    },
  },
  scales: {
    r: {
      suggestedMin: 0,
      suggestedMax: 100,
      angleLines: {
        display: true,
        color: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.05)',
      },
      grid: {
        display: false,
      },
      ticks: {
        color: 'transparent',
        backdropColor: 'transparent',
      },
      pointLabels: {
        color: isDarkMode ? '#FEFEFE' : '#111215',
        font: {
          size: 12,
          family: 'var(--font-family-base)',
        },
      },
    },
  },
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      bodyFont: {
        family: 'var(--font-family-base)',
        size: 12,
      },
      padding: 12,
      cornerRadius: 6,
      displayColors: false,
      callbacks: {
        label: (context: TooltipItem<'radar'>) => {
          const value = (context.raw as number) || 0;
          return formatTooltipValue(value);
        },
      },
    },
  },
});

// Plugin: show tooltip when hovering axis labels for radar and bar charts
export const createLabelHoverTooltipPlugin = (): Plugin => ({
  id: 'labelHoverTooltip',
  afterEvent: (chart, args) => {
    const event = args.event as unknown as {
      type: string;
      x: number;
      y: number;
    };

    if (!event || !event.type) return;

    // Clear on mouseout
    if (event.type === 'mouseout') {
      // Only clear if we were the ones activating it (safe to clear regardless)
      chart.setActiveElements([]);
      if (
        chart.tooltip &&
        typeof chart.tooltip.setActiveElements === 'function'
      ) {
        chart.tooltip.setActiveElements([], { x: 0, y: 0 });
      }
      chart.update('none');
      return;
    }

    if (event.type !== 'mousemove') return;

    const { x, y } = event;
    const datasetIndex = 0;
    const datasets = chart.data?.datasets || [];
    if (!datasets.length) return;

    // Helper to activate tooltip at index
    const activateAtIndex = (index: number) => {
      if (index < 0) return false;
      try {
        chart.setActiveElements([{ datasetIndex, index }]);
        if (
          chart.tooltip &&
          typeof chart.tooltip.setActiveElements === 'function'
        ) {
          chart.tooltip.setActiveElements([{ datasetIndex, index }], { x, y });
        }
        chart.update('none');
        return true;
      } catch {
        return false;
      }
    };

    // Radar chart label hover
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((chart.config as any).type === 'radar') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const rScale = (chart.scales as any).r as
        | (ChartJS['scales'] & {
            xCenter?: number;
            yCenter?: number;
            drawingArea: number;
          })
        | undefined;

      if (
        !rScale ||
        rScale.xCenter === undefined ||
        rScale.yCenter === undefined
      )
        return;

      const labels = chart.data?.labels as string[] | undefined;
      if (!labels || !labels.length) return;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const centerX = (rScale as any).xCenter as number;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const centerY = (rScale as any).yCenter as number;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const radius = (rScale as any).drawingArea as number;
      const dx = x - centerX;
      const dy = y - centerY;
      const distance = Math.sqrt(dx * dx + dy * dy);
      // Only react when near the label ring
      const radialInner = Math.max(0, radius - 30);
      const radialOuter = radius + 30;
      if (distance < radialInner || distance > radialOuter) return;

      const angle = Math.atan2(dy, dx); // -PI..PI, 0 at +x axis
      const twoPI = Math.PI * 2;
      const startAngle = -Math.PI / 2; // Chart.js default for radar
      const step = twoPI / labels.length;
      let normalized = angle;
      // Normalize into [startAngle, startAngle + 2PI)
      while (normalized < startAngle) normalized += twoPI;
      while (normalized >= startAngle + twoPI) normalized -= twoPI;

      const rawIndex = (normalized - startAngle) / step;
      const nearestIndex = Math.round(rawIndex) % labels.length;

      if (nearestIndex >= 0 && nearestIndex < labels.length) {
        activateAtIndex(nearestIndex);
      }
      return;
    }

    // Bar chart label hover (x-axis labels)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((chart.config as any).type === 'bar') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const xScale = (chart.scales as any).x;
      const area = chart.chartArea;
      const labels = chart.data?.labels as string[] | undefined;
      if (!xScale || !area || !labels || !labels.length) return;

      // Define a hover band near x-axis labels
      const bandHeight = 28;
      const top = area.bottom - bandHeight;
      const bottom = area.bottom + 12; // include a bit below
      if (y < top || y > bottom) return;

      const tickPositions: number[] = labels.map((_, i) =>
        xScale.getPixelForTick(i)
      );

      let targetIndex = -1;
      for (let i = 0; i < tickPositions.length; i++) {
        const current = tickPositions[i];
        const prev = i === 0 ? area.left : tickPositions[i - 1];

        const next =
          i === tickPositions.length - 1 ? area.right : tickPositions[i + 1];

        const left = (prev + current) / 2;
        const right = (current + next) / 2;
        if (x >= left && x <= right) {
          targetIndex = i;
          break;
        }
      }
      if (targetIndex !== -1) {
        activateAtIndex(targetIndex);
      }
    }
  },
});
